import { PowermeterHistoryChartData, PowermeterHistoryModel } from '../models/history-data'
import { PowermeterInfoArray } from '../models'
import { isBypass, isBypassNoCharge, isConsumption, isProduction } from 'src/Types/responses/powermeter-values/{sn}'
import { roundPositiveAndScale } from '../utils'

type Factor = 1 | 4

/**
 * Transform powermeter data (fetched from database) to rechart data so they can be displayed in a system chart.
 *
 * @param powermeters - powermeters have different types which need to be considered when transforming the data
 * @param dto
 * @param factor - 'day' view data needs to be scaled by 4
 * @param powermeterMapData - There can be multiple powermeter with data for the same timestamp. This map maps timestamp and data.
 * @returns
 */
export const fromPowermeterHistoryToRechartData = (
  powermeter: PowermeterInfoArray,
  dto: PowermeterHistoryModel[],
  factor: Factor,
  powermeterMapData: Map<number, PowermeterHistoryChartData>
): Map<number, PowermeterHistoryChartData> => {
  dto?.forEach(dataPoint => {
    const timeStampExistAlready = powermeterMapData.has(dataPoint.time)

    const { id: powermeterId, type: powermeterType } = powermeter
    const consumption = isConsumption(powermeterType) ? roundPositiveAndScale(dataPoint.NetOut, factor) : 0
    const production = isProduction(powermeterType) ? roundPositiveAndScale(dataPoint.NetIn, factor) : 0
    const bypass = isBypass(powermeterType) ? roundPositiveAndScale(dataPoint.NetIn, factor) : 0
    const byPassNoCharge = isBypassNoCharge(powermeterType) ? roundPositiveAndScale(dataPoint.NetIn, factor) : 0
    const uniqueId = powermeter?.storageId ? `_${powermeter.storageId}_${powermeterId}` : `_${powermeterId}`

    const totalPowermeterData = {
      Powermeter_consumption: consumption,
      Powermeter_production: production,
      Powermeter_NetIn_bypass: bypass,
      Powermeter_NetIn_bypass_noCharge: byPassNoCharge,
    }

    const specificPowermeterData = {
      [`pm${uniqueId}/Powermeter_consumption`]: consumption,
      [`pm${uniqueId}/Powermeter_production`]: production,
      [`pm${uniqueId}/Powermeter_NetIn_bypass`]: bypass,
      [`pm${uniqueId}/Powermeter_NetIn_bypass_noCharge`]: byPassNoCharge,
    }

    if (timeStampExistAlready) {
      const existingEntry = powermeterMapData.get(dataPoint.time)!
      existingEntry.Powermeter_consumption += consumption
      existingEntry.Powermeter_production += production

      existingEntry[`pm${uniqueId}/Powermeter_consumption`] = consumption
      existingEntry[`pm${uniqueId}/Powermeter_production`] = production
      existingEntry[`pm${uniqueId}/Powermeter_NetIn_bypass`] = bypass
      existingEntry[`pm${uniqueId}/Powermeter_NetIn_bypass_noCharge`] = byPassNoCharge
    } else {
      powermeterMapData.set(dataPoint.time, {
        ...totalPowermeterData,
        ...specificPowermeterData,
        ts: dataPoint.time,
        time: dataPoint.time,
        from: dataPoint.from,
      })
    }
  })

  return powermeterMapData
}
